import React, { Children, Component } from 'react';
import { motion } from 'framer-motion';
import { MapPinned, MessageCircle, Wifi, ArrowRight } from 'lucide-react';
import { Card, CardContent } from './Card';
import { Button } from './Button';
type Service = {
  icon: ComponentType<{
    className?: string;
  }>;
  title: string;
  description: string;
  tag: string;
};
const SERVICES: Service[] = [{
  icon: MapPinned,
  title: 'Address Availability Checks',
  description: 'Find out whether fiber internet options may be available at your Vancouver-area home before you make a decision.',
  tag: 'Start here'
}, {
  icon: Wifi,
  title: 'Fiber Plan Guidance',
  description: 'Talk through your household’s connectivity needs and compare the fiber plan options available to your address.',
  tag: 'For your home'
}, {
  icon: MessageCircle,
  title: 'Personal Consultation',
  description: 'Get straightforward answers and support from a local team focused on helping you choose with confidence.',
  tag: 'Local support'
}];
const container = {
  hidden: {},
  show: {
    transition: {
      staggerChildren: 0.08
    }
  }
};
const item = {
  hidden: {
    opacity: 0,
    y: 20
  },
  show: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.45,
      ease: [0.22, 1, 0.36, 1] as const
    }
  }
};
export function Services() {
  return <section id="services" className="w-full bg-background py-20 lg:py-28" data-id="element-135">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8" data-id="element-136">
        <div className="mx-auto max-w-2xl text-center" data-id="element-137">
          <p className="flex items-center justify-center gap-2 text-sm font-semibold uppercase tracking-wide text-brand-dark" data-id="element-138">
            <Wifi className="h-4 w-4" aria-hidden="true" data-id="element-139" /> Fiber Internet
            Services
          </p>
          <h2 className="mt-3 font-heading text-3xl font-bold tracking-tight text-foreground sm:text-4xl" data-id="element-140">
            A clearer path to fiber at home
          </h2>
          <p className="mt-4 text-lg text-muted-foreground" data-id="element-141">
            From your first availability check to choosing an option that works
            for your household, AllStar keeps the process simple.
          </p>
        </div>

        <motion.div variants={container} initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.15
      }} className="mx-auto mt-14 grid max-w-5xl grid-cols-1 gap-6 md:grid-cols-3" data-id="element-142">
          {SERVICES.map(service => {
          const Icon = service.icon;
          return <motion.div key={service.title} variants={item} data-id="element-143">
                <Card className="group h-full border-border transition-all duration-300 hover:-translate-y-1 hover:border-brand/50 hover:shadow-lg" data-id="element-144">
                  <CardContent className="flex h-full flex-col p-7" data-id="element-145">
                    <span className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-brand/10 text-brand-dark transition-colors group-hover:bg-brand group-hover:text-brand-foreground" data-id="element-146">
                      <Icon className="h-6 w-6" data-id="element-147" />
                    </span>
                    <span className="mt-6 text-xs font-semibold uppercase tracking-wide text-brand-dark" data-id="element-148">
                      {service.tag}
                    </span>
                    <h3 className="mt-2 font-heading text-xl font-semibold text-foreground" data-id="element-149">
                      {service.title}
                    </h3>
                    <p className="mt-3 flex-1 leading-relaxed text-muted-foreground" data-id="element-150">
                      {service.description}
                    </p>
                  </CardContent>
                </Card>
              </motion.div>;
        })}
        </motion.div>

        <div className="mt-12 text-center" data-id="element-151">
          <Button asChild size="lg" className="h-12 bg-brand px-7 text-base text-brand-foreground hover:bg-brand-dark" data-id="element-152">
            <a href="#contact" data-id="element-153">
              Request an availability check{' '}
              <ArrowRight className="h-4 w-4" aria-hidden="true" data-id="element-154" />
            </a>
          </Button>
        </div>
      </div>
    </section>;
}